import { checkAuthToken } from "../services/hooks/preauth/checkAuthToken";
import HubService from "../services/api/HubService";
import { successfulLoginActions } from "../services/hooks/preauth/useLogin";
import { useAuthStore } from "@/client/services/state/authStore";
import { useEffect } from "react";
import { useHandoffParams } from "../services/hooks/preauth/useHandoffParams";
import { useHubSessionStore } from "@/client/services/state/hubSessionStore";
import { useNavigate } from "react-router-dom";
import { usePartnerStore } from "@/client/services/state/partnerStore";
import { useToastStore } from "../services/state/toastStore";
import { useTranslation } from "react-i18next";

/**
 * Hub access handoff landing — runs on the CHILD org's domain, outside any
 * layout (peer of OauthTransition/SamlTransition). The parent app opened this
 * tab with a single-use, short-TTL code; exchange it for a child-scoped mirror
 * token, hydrate the full AuthConfig, and boot the session. Overwrites any
 * existing session on this origin (accepted for v1).
 */
export default function HubTransition() {
  const { authConfig, setAuthConfig } = useAuthStore();
  const { clearPartnerStore } = usePartnerStore();
  const { startHubSession, clearHubSession } = useHubSessionStore();
  const navigate = useNavigate();
  const { setToast } = useToastStore();
  const { t } = useTranslation();
  const params = useHandoffParams();

  useEffect(() => {
    // Sever the opener link: the parent console opened this tab, and this is a
    // cross-company boundary — the child origin must never be able to steer
    // the parent admin's tab (reverse tabnabbing).
    if (window.opener) window.opener = null;

    const code = params.get("code");

    // Cleared up front so EVERY non-success exit (invalid code, suspended
    // child, thrown exchange) leaves no stale hub flag from a prior handoff;
    // startHubSession re-sets it on the success path only.
    clearHubSession();

    const failToLogin = () => {
      setToast({
        show: true,
        status: "error",
        title: t("hubs.transition.error"),
      });
      navigate("/login");
    };

    if (!code) {
      failToLogin();
      return;
    }

    const run = async () => {
      try {
        const { token } = await HubService.exchangeCode(code);
        const resultObj = await checkAuthToken(token);

        if (!resultObj.result) {
          if (resultObj.navigate) {
            navigate(resultObj.navigate);
            return;
          }
          failToLogin();
          return;
        }

        // A stale partner on this origin trips LoggedInLayout's
        // partner-mismatch force-logout; mirror users never carry a partner.
        clearPartnerStore();
        startHubSession(resultObj.data.user._id);

        await successfulLoginActions(
          resultObj.data,
          authConfig,
          setAuthConfig,
          null,
          "/admin/content",
          navigate
        );
      } catch {
        failToLogin();
      }
    };
    run();
  }, []);

  return <div />;
}
